import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Globe, MapPin, Tag, Building2, Music2, Ticket, Calendar, Banknote, Settings, Sparkles, LogOut, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

export default function AdminLayout() {
    const { t } = useTranslation();
    const { signOut } = useAuth();
    const navigate = useNavigate();

    const links = [
        { to: "/admin", label: t("admin.dashboard"), icon: Home, end: true, tid: "admin-nav-dashboard" },
        { to: "/admin/events", label: t("admin.events"), icon: Calendar, tid: "admin-nav-events" },
        { to: "/admin/bookings", label: t("admin.bookings"), icon: Ticket, tid: "admin-nav-bookings" },
        { to: "/admin/top-events", label: t("admin.top_events"), icon: Sparkles, tid: "admin-nav-top-events" },
        { to: "/admin/payouts", label: t("admin.payouts"), icon: Banknote, tid: "admin-nav-payouts" },
        { to: "/admin/reference", label: `${t("admin.states")} / ${t("admin.cities")} / ${t("admin.categories")}`, icon: Globe, tid: "admin-nav-reference" },
        { to: "/admin/venues", label: t("admin.venues"), icon: Building2, tid: "admin-nav-venues" },
        { to: "/admin/artists", label: t("admin.artists"), icon: Music2, tid: "admin-nav-artists" },
        { to: "/admin/system", label: t("admin.system"), icon: Settings, tid: "admin-nav-system" },
    ];

    return (
        <div data-testid="admin-layout" className="min-h-screen bg-muted/30 flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 border-r border-border bg-white min-h-screen flex-col">
                <Link to="/admin" className="p-5 flex items-center border-b border-border">
                    <img src="/logo-youvibeticket-clr.png" alt="youvibeticket" className="h-8 w-auto" />
                </Link>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {links.map(({ to, label, icon: Icon, end, tid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={tid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-foreground text-background" : "hover:bg-muted"}`
                            }
                        >
                            <Icon className="h-4 w-4" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-border space-y-1">
                    <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted">
                        <User className="h-4 w-4" />
                        Back to site
                    </Link>
                    <button data-testid="admin-logout" onClick={async () => { await signOut(); navigate("/"); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-muted">
                        <LogOut className="h-4 w-4" />
                        {t("nav.logout")}
                    </button>
                </div>
            </aside>
            {/* Content */}
            <main className="flex-1 overflow-x-hidden">
                {/* Mobile header */}
                <div className="md:hidden border-b border-border bg-white p-4 flex items-center justify-between">
                    <Link to="/admin" className="flex items-center">
                        <img src="/logo-youvibeticket-clr.png" alt="youvibeticket" className="h-7 w-auto" />
                    </Link>
                    <button
                        data-testid="m-admin-logout"
                        onClick={async () => { await signOut(); navigate("/"); }}
                        className="p-2 rounded-lg hover:bg-muted text-sm font-semibold inline-flex items-center gap-1.5"
                        aria-label={t("nav.logout")}
                    >
                        <LogOut className="h-4 w-4" />
                        <span className="text-xs">{t("nav.logout")}</span>
                    </button>
                </div>
                {/* Mobile nav grid */}
                <div className="md:hidden border-b border-border bg-white p-2">
                    <div className="grid grid-cols-2 gap-1.5">
                        {links.map(({ to, label, icon: Icon, end, tid }) => (
                            <NavLink
                                key={to}
                                to={to}
                                end={end}
                                data-testid={`m-${tid}`}
                                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${isActive ? "bg-foreground text-background" : "bg-muted"}`}
                            >
                                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                                <span className="truncate">{label}</span>
                            </NavLink>
                        ))}
                    </div>
                </div>
                <div className="p-4 md:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
